import { defineNuxtPlugin, useRhythmicLogger, useRouter } from '#imports'
import { useBacklogContext } from '../../contexts/backlog/useBacklogContext'
import { useDocumentContext } from '../../contexts/document/useDocumentContext'
import useHarmonyPlanContext from '../../contexts/harmonyPlan/useHarmonyPlanContext'
import useKanbanBoardContext from '../../contexts/kanbanBoard/useKanbanBoardContext'
import useTeamContext from '../../contexts/team/useTeamContext'

type RouteParams = Record<string, unknown>

type ContextRouterGlobal = typeof globalThis & {
  __rhythmicContextRouterInstalled?: boolean
}

function normalizeParam(value: unknown, strategy: 'first' | 'last' = 'last'): string | undefined {
  const resolvedValue = Array.isArray(value) ? (strategy === 'last' ? value.at(-1) : value[0]) : value

  if (typeof resolvedValue !== 'string') return undefined

  const trimmed = resolvedValue.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function resolveParam(params: RouteParams, keys: string[], strategy: 'first' | 'last' = 'last') {
  for (const key of keys) {
    const normalized = normalizeParam(params[key], strategy)
    if (normalized !== undefined) return normalized
  }

  return undefined
}

export default defineNuxtPlugin(() => {
  if (!import.meta.client) return

  const globalState = globalThis as ContextRouterGlobal
  if (globalState.__rhythmicContextRouterInstalled) return

  globalState.__rhythmicContextRouterInstalled = true

  const logger = useRhythmicLogger('contextRouterPlugin')
  const router = useRouter()

  const teamContext = useTeamContext()
  const backlogContext = useBacklogContext()
  const harmonyPlanContext = useHarmonyPlanContext()
  const kanbanBoardContext = useKanbanBoardContext()
  const documentContext = useDocumentContext()

  logger.debug('Initializing unified context router plugin')

  function syncRoute(to: { fullPath: string; params: RouteParams }) {
    const path = to.fullPath
    const nextWorkspaceSlug = resolveParam(to.params, ['workspace', 'workspaceSlug'])
    const nextTeamSlug = resolveParam(to.params, ['teamSlug', 'team'])
    const nextHarmonyPlanId = resolveParam(to.params, ['id', 'planId', 'harmonyPlanId'])
    const nextBoardId = resolveParam(to.params, ['boardId'], 'first')
    const nextDocumentId = resolveParam(to.params, ['docId', 'documentId'])

    const previous = {
      team: {
        workspaceSlug: teamContext.workspaceSlug,
        teamSlug: teamContext.teamSlug,
        harmonyPlanId: teamContext.harmonyPlanId,
      },
      backlog: {
        workspaceSlug: backlogContext.workspaceSlug,
        teamSlug: backlogContext.teamSlug,
      },
      harmonyPlan: {
        harmonyPlanId: harmonyPlanContext.harmonyPlanId,
      },
      kanban: {
        harmonyPlanId: kanbanBoardContext.harmonyPlanId,
        boardId: kanbanBoardContext.boardId,
      },
      document: {
        documentId: documentContext.documentId,
      },
    }

    const workspaceChanged = previous.team.workspaceSlug !== nextWorkspaceSlug
    const teamChanged = previous.team.teamSlug !== nextTeamSlug
    const planChanged = previous.team.harmonyPlanId !== nextHarmonyPlanId
    const backlogWorkspaceChanged = previous.backlog.workspaceSlug !== nextWorkspaceSlug
    const backlogTeamChanged = previous.backlog.teamSlug !== nextTeamSlug
    const harmonyPlanIdChanged = previous.harmonyPlan.harmonyPlanId !== nextHarmonyPlanId
    const kanbanPlanChanged = previous.kanban.harmonyPlanId !== nextHarmonyPlanId
    const kanbanBoardChanged = previous.kanban.boardId !== nextBoardId
    const documentIdChanged = previous.document.documentId !== nextDocumentId

    if (
      !workspaceChanged &&
      !teamChanged &&
      !planChanged &&
      !backlogWorkspaceChanged &&
      !backlogTeamChanged &&
      !harmonyPlanIdChanged &&
      !kanbanPlanChanged &&
      !kanbanBoardChanged &&
      !documentIdChanged
    )
      return

    if (workspaceChanged || teamChanged || planChanged) {
      logger.debug('Route changed, syncing team context params', {
        path,
        previousWorkspaceSlug: previous.team.workspaceSlug,
        nextWorkspaceSlug,
        previousTeamSlug: previous.team.teamSlug,
        nextTeamSlug,
        previousHarmonyPlanId: previous.team.harmonyPlanId,
        nextHarmonyPlanId,
      })

      teamContext.workspaceSlug = nextWorkspaceSlug
      teamContext.teamSlug = nextTeamSlug
      teamContext.harmonyPlanId = nextHarmonyPlanId

      if (workspaceChanged) {
        void teamContext.queries.emitWorkspaceChanged({
          path,
          previousWorkspaceSlug: previous.team.workspaceSlug,
          workspaceSlug: nextWorkspaceSlug,
        })
      }

      if (teamChanged) {
        void teamContext.queries.emitTeamChanged({
          teamSlug: nextTeamSlug,
          previousTeamSlug: previous.team.teamSlug,
          teamId: undefined,
          workspaceId: undefined,
          team: undefined,
        })
      }

      if (planChanged) {
        void teamContext.queries.emitPlanIdChanged({
          harmonyPlanId: nextHarmonyPlanId,
          previousHarmonyPlanId: previous.team.harmonyPlanId,
        })
      }
    }

    if (backlogWorkspaceChanged || backlogTeamChanged) {
      logger.debug('Route changed, syncing backlog context params', {
        path,
        previousWorkspaceSlug: previous.backlog.workspaceSlug,
        nextWorkspaceSlug,
        previousTeamSlug: previous.backlog.teamSlug,
        nextTeamSlug,
      })

      backlogContext.workspaceSlug = nextWorkspaceSlug
      backlogContext.teamSlug = nextTeamSlug

      if (backlogWorkspaceChanged) {
        void backlogContext.queries.emitWorkspaceSlugChanged({
          path,
          previousWorkspaceSlug: previous.backlog.workspaceSlug,
          workspaceSlug: nextWorkspaceSlug,
        })
      }

      if (backlogTeamChanged) {
        void backlogContext.queries.emitTeamSlugChanged({
          path,
          previousTeamSlug: previous.backlog.teamSlug,
          teamSlug: nextTeamSlug,
        })
      }
    }

    if (harmonyPlanIdChanged) {
      logger.debug('Route changed, syncing harmony plan context params', {
        path,
        previousHarmonyPlanId: previous.harmonyPlan.harmonyPlanId,
        nextHarmonyPlanId,
      })

      harmonyPlanContext.harmonyPlanId = nextHarmonyPlanId

      void harmonyPlanContext.queries.emitPlanIdChanged({
        path,
        harmonyPlanId: nextHarmonyPlanId,
        previousHarmonyPlanId: previous.harmonyPlan.harmonyPlanId,
      })
    }

    if (kanbanPlanChanged || kanbanBoardChanged) {
      logger.debug('Route changed, syncing kanban board context params', {
        path,
        previousHarmonyPlanId: previous.kanban.harmonyPlanId,
        nextHarmonyPlanId,
        previousBoardId: previous.kanban.boardId,
        nextBoardId,
      })

      kanbanBoardContext.harmonyPlanId = nextHarmonyPlanId
      kanbanBoardContext.boardId = nextBoardId

      if (kanbanPlanChanged) {
        void kanbanBoardContext.queries.emitPlanIdChanged({
          path,
          harmonyPlanId: nextHarmonyPlanId,
          previousHarmonyPlanId: previous.kanban.harmonyPlanId,
        })
      }

      if (kanbanBoardChanged) {
        void kanbanBoardContext.queries.emitBoardIdChanged({
          path,
          boardId: nextBoardId,
          previousBoardId: previous.kanban.boardId,
        })
      }
    }

    if (documentIdChanged) {
      logger.debug('Route changed, syncing document context params', {
        path,
        previousDocumentId: previous.document.documentId,
        nextDocumentId,
      })

      documentContext.documentId = nextDocumentId

      void documentContext.queries.emitDocumentIdChanged({
        path,
        documentId: nextDocumentId,
        previousDocumentId: previous.document.documentId,
      })
    }
  }

  syncRoute(router.currentRoute.value)

  const removeAfterEach = router.afterEach((to) => {
    syncRoute(to)
  })

  void router
    .isReady()
    .then(() => syncRoute(router.currentRoute.value))
    .catch((error) => {
      logger.error('Failed to sync initial route for contexts', { error })
    })

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      removeAfterEach()
    })
  }
})
